import { apiRequest, apiRequestPage } from './client';

/**
 * Clients for the Companies registry (FEAT-011) and the Employees directory
 * (FEAT-012). They share a file because every employee screen needs the company
 * list for its pickers, and splitting them would mean one importing the other.
 */

export interface Company {
  id: string;
  name: string;
  cr_number: string;
  website: string | null;
  is_house: boolean;
  created_on: string;
  employee_count: number;
}

/** What a delete would affect, shown before it happens (REQ-066). */
export interface CompanyImpact {
  id: string;
  name: string;
  is_house: boolean;
  employees: number;
  tickets: number;
  can_delete: boolean;
}

export interface Employee {
  id: string;
  name: string;
  email: string;
  role: string;
  employer_id: string;
  employer_name: string;
  office: string | null;
  employee_no: string | null;
  phone: string;
  status: 'invited' | 'active' | 'resigned';
  is_comm_contact: boolean;
  is_system_chief: boolean;
}

export async function listCompanies(params: {
  page: number;
  pageSize: number;
  q?: string;
}): Promise<{ items: Company[]; total: number }> {
  const { items, meta } = await apiRequestPage<Company>('/companies', {
    query: { page: params.page, page_size: params.pageSize, q: params.q },
  });
  return { items, total: meta.total_items };
}

export function createCompany(body: {
  name: string;
  cr_number: string;
  website?: string;
}): Promise<Company> {
  return apiRequest<Company>('/companies', { method: 'POST', body });
}

export function updateCompany(
  id: string,
  body: { name?: string; cr_number?: string; website?: string },
): Promise<Company> {
  return apiRequest<Company>(`/companies/${id}`, { method: 'PATCH', body });
}

export function companyImpact(id: string): Promise<CompanyImpact> {
  return apiRequest<CompanyImpact>(`/companies/${id}/impact`);
}

export function deleteCompany(id: string): Promise<void> {
  return apiRequest<void>(`/companies/${id}`, { method: 'DELETE' });
}

export async function listEmployees(params: {
  page: number;
  pageSize: number;
  employer?: string;
  status?: Employee['status'];
}): Promise<{ items: Employee[]; total: number }> {
  const { items, meta } = await apiRequestPage<Employee>('/employees', {
    query: {
      page: params.page,
      page_size: params.pageSize,
      employer: params.employer,
      status: params.status,
    },
  });
  return { items, total: meta.total_items };
}

/**
 * The roles this caller may assign at this company.
 *
 * Fetched rather than derived client-side: the three-axis rule (by caller, by
 * company, by target) is recomputed server-side on every save anyway, and a
 * second implementation here would be one more thing that can disagree with it.
 */
export function assignableRoles(companyId: string): Promise<{ roles: string[] }> {
  return apiRequest<{ roles: string[] }>('/employees/assignable-roles', {
    query: { company: companyId },
  });
}

export function inviteEmployee(body: {
  name: string;
  email: string;
  employer_id: string;
  role: string;
  phone: string;
  fingerprint_id: string;
  office?: string;
}): Promise<Employee> {
  return apiRequest<Employee>('/employees', { method: 'POST', body });
}

export function changeEmployeeRole(id: string, role: string): Promise<Employee> {
  return apiRequest<Employee>(`/employees/${id}/role`, { method: 'POST', body: { role } });
}

export function resignEmployee(id: string): Promise<Employee> {
  return apiRequest<Employee>(`/employees/${id}/resign`, { method: 'POST' });
}

export function reinstateEmployee(id: string): Promise<Employee> {
  return apiRequest<Employee>(`/employees/${id}/reinstate`, { method: 'POST' });
}

/**
 * What a permanent deletion would destroy (REQ-073).
 *
 * Fetched before the confirmation is shown, not after. This one cannot be
 * undone, so an administrator should be told "and four meetings other people are
 * attending" *before* they press it.
 */
export interface EmployeeImpact {
  id: string;
  name: string;
  bookings: number;
  upcoming_bookings: number;
  tickets: number;
}

export function employeeImpact(id: string): Promise<EmployeeImpact> {
  return apiRequest<EmployeeImpact>(`/employees/${id}/impact`);
}

/** Irreversible. Destroys their bookings and support requests with them. */
export function destroyEmployee(id: string): Promise<void> {
  return apiRequest<void>(`/employees/${id}/permanent`, { method: 'DELETE' });
}

/** The API caps `page_size` at 100. */
const MAX_PAGE_SIZE = 100;

/**
 * Every page of a list, for the pickers.
 *
 * The room allow-lists and the meeting-invitee picker have to offer *everyone*,
 * and a single request cannot: asking for a page size above the cap is a 422,
 * and silently asking for the first 100 would drop people from the picker with
 * no sign that anything was missing — the worst of the three outcomes, because
 * it looks like it worked.
 */
async function fetchAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ items: T[]; total: number }>,
): Promise<T[]> {
  const first = await fetchPage(1, MAX_PAGE_SIZE);
  const pages = Math.ceil(first.total / MAX_PAGE_SIZE);

  if (pages <= 1) return first.items;

  const rest = await Promise.all(
    Array.from({ length: pages - 1 }, (_, i) => fetchPage(i + 2, MAX_PAGE_SIZE)),
  );

  return [first.items, ...rest.map((page) => page.items)].flat();
}

export function listAllCompanies(): Promise<Company[]> {
  return fetchAllPages((page, pageSize) => listCompanies({ page, pageSize }));
}

export function listAllEmployees(): Promise<Employee[]> {
  return fetchAllPages((page, pageSize) => listEmployees({ page, pageSize }));
}

/**
 * The people the signed-in user may invite to a meeting.
 *
 * Deliberately not `listAllEmployees`, which reads the HR directory and needs
 * `Employees.View` — a capability an ordinary employee does not hold, so that
 * call 403'd and the picker came back empty with no way to invite anybody.
 *
 * One request rather than paging: it returns two fields per person, so even a
 * large building is a small response, and the picker needs all of it at once to
 * filter as somebody types.
 */
export function listInvitablePeople(): Promise<{ id: string; name: string }[]> {
  return apiRequest<{ id: string; name: string }[]>('/employees/lookup');
}
